import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { t } from '../../utils/localization/localization';
import { useLanguageAwareRender } from '../../hooks/useLanguageAwareRender';
import { createErrorLogger, ErrorType } from '../../utils/logger';
import reprocessingService from '../../services/reprocessingService';

const logger = createErrorLogger('ReprocessingManagement');

function buildSummary(result) {
  const users = Array.isArray(result?.users) ? result.users : [];
  return {
    total: result?.total_processed ?? users.length,
    users: users.map((entry) => entry.user_id).join(', ')
  };
}

export default function ReprocessingManagement() {
  const languageRenderKey = useLanguageAwareRender('ReprocessingManagement');
  const [error, setError] = useState('');
  const [cvRunning, setCvRunning] = useState(false);
  const [jobRunning, setJobRunning] = useState(false);
  const [cvSummary, setCvSummary] = useState(null);
  const [jobSummary, setJobSummary] = useState(null);

  const handleCvReprocess = async () => {
    setError('');
    setCvSummary(null);
    setCvRunning(true);
    try {
      const response = await reprocessingService.reprocessAllCVs();
      setCvSummary(buildSummary(response?.result));
    } catch (serviceError) {
      const detail = serviceError?.response?.data?.detail;
      logger.error('handleCvReprocess', 'CV reprocess failed', { detail, message: serviceError.message }, ErrorType.API_CALL);
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail || serviceError.message));
    } finally {
      setCvRunning(false);
    }
  };

  const handleJobReprocess = async () => {
    setError('');
    setJobSummary(null);
    setJobRunning(true);
    try {
      const response = await reprocessingService.reprocessAllJobsWithConcordance();
      setJobSummary(buildSummary(response?.result));
    } catch (serviceError) {
      const detail = serviceError?.response?.data?.detail;
      logger.error('handleJobReprocess', 'Job reprocess failed', { detail, message: serviceError.message }, ErrorType.API_CALL);
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail || serviceError.message));
    } finally {
      setJobRunning(false);
    }
  };

  return (
    <Box key={languageRenderKey}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        {t('admin.reprocessing.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('admin.reprocessing.description')}
      </Typography>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {t('admin.reprocessing.cvTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('admin.reprocessing.cvDescription')}
        </Typography>
        <Button
          variant="contained"
          startIcon={cvRunning ? <CircularProgress size={16} /> : <ReplayIcon />}
          onClick={handleCvReprocess}
          disabled={cvRunning || jobRunning}
        >
          {t('admin.reprocessing.cvAction')}
        </Button>
        {cvSummary && (
          <Typography variant="body2" sx={{ mt: 2 }}>
            {t('admin.reprocessing.completedSummary', { total: cvSummary.total, users: cvSummary.users })}
          </Typography>
        )}
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {t('admin.reprocessing.jobTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('admin.reprocessing.jobDescription')}
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          startIcon={jobRunning ? <CircularProgress size={16} /> : <AutorenewIcon />}
          onClick={handleJobReprocess}
          disabled={jobRunning || cvRunning}
        >
          {t('admin.reprocessing.jobAction')}
        </Button>
        {jobSummary && (
          <Typography variant="body2" sx={{ mt: 2 }}>
            {t('admin.reprocessing.completedSummary', { total: jobSummary.total, users: jobSummary.users })}
          </Typography>
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
    </Box>
  );
}
